import type { ReactNode } from 'react';
import { Button } from './button';
import { IconAlert, IconInbox, IconWrench } from './icons';
import styles from './empty-state.module.css';

export interface EmptyStateProps {
  title: string;
  body?: ReactNode;
  icon?: ReactNode;
  actions?: ReactNode;
  compact?: boolean;
}

export function EmptyState({ title, body, icon, actions, compact }: EmptyStateProps) {
  return (
    <div className={[styles.wrap, compact ? styles.compact : null].filter(Boolean).join(' ')}>
      <span className={styles.mark} aria-hidden="true">
        {icon ?? <IconInbox />}
      </span>
      <h3 className={styles.title}>{title}</h3>
      {body ? <p className={styles.body}>{body}</p> : null}
      {actions ? <div className={styles.actions}>{actions}</div> : null}
    </div>
  );
}

export interface ErrorStateProps {
  title?: string;
  message: string;
  onRetry?: () => void;
  compact?: boolean;
}

export function ErrorState({
  title = 'That did not load',
  message,
  onRetry,
  compact,
}: ErrorStateProps) {
  return (
    <div
      className={[styles.wrap, styles.error, compact ? styles.compact : null]
        .filter(Boolean)
        .join(' ')}
      role="alert"
    >
      <span className={styles.mark} aria-hidden="true">
        <IconAlert />
      </span>
      <h3 className={styles.title}>{title}</h3>
      <p className={styles.detail}>{message}</p>
      {onRetry ? (
        <div className={styles.actions}>
          <Button variant="secondary" onClick={onRetry}>
            Try again
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The honest placeholder. Used by every section that is routed and navigable
 * but not yet implemented. It never shows invented data — a screen that lies
 * about being finished is worse than a screen that admits it is not.
 */
export function NotBuiltYet({
  section,
  summary,
  planned,
}: {
  section: string;
  summary: string;
  planned?: string[];
}) {
  return (
    <div className={[styles.wrap, styles.pending].join(' ')}>
      <span className={styles.mark} aria-hidden="true">
        <IconWrench />
      </span>
      <h3 className={styles.title}>{section} is not built yet</h3>
      <p className={styles.body}>{summary}</p>
      {planned && planned.length > 0 ? (
        <ul className={styles.pendingList}>
          {planned.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : null}
      <p className={styles.detail}>No data is shown here because none of it is real yet.</p>
    </div>
  );
}

/**
 * A page that works, whose backing feature is only partly real.
 *
 * Distinct from `NotBuiltYet`, which *replaces* a screen. These sections fetch
 * and render genuine data — what is missing sits behind them, so replacing the
 * page would be a lie in the other direction. This qualifies what is on screen
 * instead: the data below is real, this named part of the feature is not.
 *
 * Sourced from the README's "Not built — do not promise these to a client"
 * list, so the claim is traceable to something the repo already asserts rather
 * than to whoever last edited the page.
 */
export function PartialFeature({ title, body }: { title: string; body: string }) {
  return (
    <div className={styles.partial} role="note">
      <span className={styles.partialIcon} aria-hidden="true">
        <IconAlert size={18} />
      </span>
      <span className={styles.partialText}>
        <span className={styles.partialTitle}>{title}</span>
        <span className={styles.partialBody}>{body}</span>
      </span>
    </div>
  );
}
